/**
 * Weather Records Module
 * Fetches and displays yearly and all-time weather records
 * Tracks real-time observations to update summary when exceeded
 */

import { REFRESH_INTERVALS, API_ENDPOINTS } from '../config.js';

// Store current summary values for real-time comparison
const currentSummary = {
  today: {
    temperature: { high: null, low: null },
    wind: { max: null },
    precipitation: { total: null },
  },
  month: {
    temperature: { high: null, low: null },
    wind: { max: null },
    precipitation: { total: null },
  },
};

// Store original API summary values (before real-time updates)
let apiSummary = null;

// Track which values have been exceeded by real-time observations (with timestamps)
const realtimeExceeded = {
  today: {
    tempHigh: null,  // null = not exceeded, timestamp = when exceeded
    tempLow: null,
    windMax: null,
    precipTotal: null,
  },
};

// Refresh interval reference
let refreshInterval = null;

/**
 * Fetch records from API
 */
export async function fetchRecords() {
  try {
    const response = await fetch(API_ENDPOINTS.TEMPEST_STATS);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[Records] Failed to fetch records:', error);
    return null;
  }
}

/**
 * Update stored summary values from API response
 */
function updateStoredSummary(summary) {
  if (!summary) {
    return;
  }

  // Store the API values
  apiSummary = JSON.parse(JSON.stringify(summary));

  // Reset real-time exceeded timestamps when we get fresh API data
  realtimeExceeded.today = {
    tempHigh: null,
    tempLow: null,
    windMax: null,
    precipTotal: null,
  };

  // Update current summary with API values
  if (summary.today) {
    if (summary.today.temperature) {
      currentSummary.today.temperature.high = summary.today.temperature.high?.f ?? null;
      currentSummary.today.temperature.low = summary.today.temperature.low?.f ?? null;
    }
    if (summary.today.wind) {
      currentSummary.today.wind.max = summary.today.wind.max?.mph ?? null;
    }
    if (summary.today.precipitation) {
      currentSummary.today.precipitation.total = summary.today.precipitation.total?.in ?? null;
    }
  }

  if (summary.month) {
    if (summary.month.temperature) {
      currentSummary.month.temperature.high = summary.month.temperature.high?.f ?? null;
      currentSummary.month.temperature.low = summary.month.temperature.low?.f ?? null;
    }
    if (summary.month.wind) {
      currentSummary.month.wind.max = summary.month.wind.max?.mph ?? null;
    }
    if (summary.month.precipitation) {
      currentSummary.month.precipitation.total = summary.month.precipitation.total?.in ?? null;
    }
  }
}

/**
 * Check if a real-time observation exceeds current summary values
 * @param {string} type - 'temperature', 'wind', or 'precipitation'
 * @param {number} value - The observed value (in display units: F, mph, inches)
 * @param {number} [timestamp] - Unix timestamp of the observation (defaults to current time)
 * @returns {boolean} True if the value exceeded and display was updated
 */
export function checkAndUpdateSummary(type, value, timestamp) {
  if (value === null || value === undefined) {
    return false;
  }

  let updated = false;

  const obsTime = timestamp || Math.floor(Date.now() / 1000);

  switch (type) {
  case 'temperature':
    // Check high temperature
    if (currentSummary.today.temperature.high === null ||
          value > currentSummary.today.temperature.high) {
      currentSummary.today.temperature.high = value;
      realtimeExceeded.today.tempHigh = obsTime;
      updated = true;
    }
    // Check low temperature
    if (currentSummary.today.temperature.low === null ||
          value < currentSummary.today.temperature.low) {
      currentSummary.today.temperature.low = value;
      realtimeExceeded.today.tempLow = obsTime;
      updated = true;
    }
    break;

  case 'wind':
    // Check max wind gust
    if (currentSummary.today.wind.max === null ||
          value > currentSummary.today.wind.max) {
      currentSummary.today.wind.max = value;
      realtimeExceeded.today.windMax = obsTime;
      updated = true;
    }
    break;

  case 'precipitation':
    // Check total precipitation
    if (currentSummary.today.precipitation.total === null ||
          value > currentSummary.today.precipitation.total) {
      currentSummary.today.precipitation.total = value;
      realtimeExceeded.today.precipTotal = obsTime;
      updated = true;
    }
    break;
  }

  // If values were updated, refresh the summary display
  if (updated) {
    refreshSummaryDisplay();
  }

  return updated;
}

/**
 * Get current summary values (for external access)
 */
export function getCurrentSummary() {
  return currentSummary;
}

/**
 * Get real-time exceeded flags (for styling)
 */
export function getRealtimeExceeded() {
  return realtimeExceeded;
}

/**
 * Format timestamp to readable date
 */
function formatDate(timestamp) {
  if (!timestamp) {
    return 'N/A';
  }
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format timestamp to readable time (for today's summary)
 */
function formatTime(timestamp) {
  if (!timestamp) {
    return '—';
  }
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Create records table for a specific period
 */
function createRecordsTable(records, period) {
  const table = document.createElement('table');
  table.className = 'records-table';

  let html = `
    <thead>
      <tr>
        <th>Record</th>
        <th>Value</th>
        <th>Date</th>
      </tr>
    </thead>
    <tbody>
  `;

  const data = period === 'yearly' ? 'yearly' : 'alltime';

  // Temperature High
  if (records.temperature) {
    const highF = records.temperature[data].high?.f;
    const lowF = records.temperature[data].low?.f;
    html += `
      <tr class="record-high">
        <td>🌡️ High Temp</td>
        <td><strong>${highF != null ? highF.toFixed(1) : 'N/A'}°F</strong></td>
        <td>${formatDate(records.temperature[data].high_time)}</td>
      </tr>
      <tr class="record-low">
        <td>🌡️ Low Temp</td>
        <td><strong>${lowF != null ? lowF.toFixed(1) : 'N/A'}°F</strong></td>
        <td>${formatDate(records.temperature[data].low_time)}</td>
      </tr>
    `;
  }

  // Wind Gust
  if (records.wind) {
    html += `
      <tr class="record-wind">
        <td>💨 Wind Gust</td>
        <td><strong>${records.wind[data].max?.mph?.toFixed(1) || 'N/A'} mph</strong></td>
        <td>${formatDate(records.wind[data].max_time)}</td>
      </tr>
    `;
  }

  // Daily Precipitation
  if (records.precipitation) {
    html += `
      <tr class="record-precip">
        <td>🌧️ Daily Precip</td>
        <td><strong>${records.precipitation[data].max?.in?.toFixed(2) || 'N/A'} in</strong></td>
        <td>${formatDate(records.precipitation[data].max_time)}</td>
      </tr>
    `;
  }

  html += `
    </tbody>
  `;

  table.innerHTML = html;
  return table;
}

/**
 * Create summary table for a specific period
 * For 'today' period, uses real-time tracked values from currentSummary
 */
function createSummaryTable(summary, period) {
  const table = document.createElement('table');
  table.className = 'records-table';

  const data = summary[period];
  if (!data) {
    return table;
  }

  const precipLabel = period === 'today' ? 'Daily Total' : 'Monthly Total';
  const timeHeader = period === 'today' ? 'Time' : 'Date';
  const formatTimeOrDate = period === 'today' ? formatTime : formatDate;

  // For today, use real-time tracked values; for month, use API values
  const useRealtime = period === 'today';
  const exceeded = realtimeExceeded.today;

  let html = `
    <thead>
      <tr>
        <th>Metric</th>
        <th>Value</th>
        <th>${timeHeader}</th>
      </tr>
    </thead>
    <tbody>
  `;

  // Temperature High/Low
  if (data.temperature || (useRealtime && currentSummary.today.temperature)) {
    const highTemp = useRealtime
      ? currentSummary.today.temperature.high
      : data.temperature?.high?.f;
    const lowTemp = useRealtime
      ? currentSummary.today.temperature.low
      : data.temperature?.low?.f;
    const highClass = useRealtime && exceeded.tempHigh ? 'record-high realtime-updated' : 'record-high';
    const lowClass = useRealtime && exceeded.tempLow ? 'record-low realtime-updated' : 'record-low';

    // Use realtime timestamp if exceeded, otherwise API timestamp
    const highTime = useRealtime && exceeded.tempHigh
      ? formatTime(exceeded.tempHigh)
      : formatTimeOrDate(data.temperature?.high_time);
    const lowTime = useRealtime && exceeded.tempLow
      ? formatTime(exceeded.tempLow)
      : formatTimeOrDate(data.temperature?.low_time);

    html += `
      <tr class="${highClass}">
        <td>🌡️ High Temp</td>
        <td><strong>${highTemp != null ? highTemp.toFixed(1) : 'N/A'}°F</strong></td>
        <td>${highTime}</td>
      </tr>
      <tr class="${lowClass}">
        <td>🌡️ Low Temp</td>
        <td><strong>${lowTemp != null ? lowTemp.toFixed(1) : 'N/A'}°F</strong></td>
        <td>${lowTime}</td>
      </tr>
    `;
  }

  // Wind Gust
  if (data.wind || (useRealtime && currentSummary.today.wind)) {
    const windMax = useRealtime
      ? currentSummary.today.wind.max
      : data.wind?.max?.mph;
    const windClass = useRealtime && exceeded.windMax ? 'record-wind realtime-updated' : 'record-wind';

    // Use realtime timestamp if exceeded, otherwise API timestamp
    const windTime = useRealtime && exceeded.windMax
      ? formatTime(exceeded.windMax)
      : formatTimeOrDate(data.wind?.max_time);

    html += `
      <tr class="${windClass}">
        <td>💨 Wind Gust</td>
        <td><strong>${windMax !== null ? windMax.toFixed(1) : 'N/A'} mph</strong></td>
        <td>${windTime}</td>
      </tr>
    `;
  }

  // Precipitation Total
  if (data.precipitation || (useRealtime && currentSummary.today.precipitation)) {
    const precipTotal = useRealtime
      ? currentSummary.today.precipitation.total
      : data.precipitation?.total?.in;
    const precipClass = useRealtime && exceeded.precipTotal ? 'record-precip realtime-updated' : 'record-precip';

    html += `
      <tr class="${precipClass}">
        <td>🌧️ ${precipLabel}</td>
        <td><strong>${precipTotal !== null ? precipTotal.toFixed(2) : 'N/A'} in</strong></td>
        <td>—</td>
      </tr>
    `;
  }

  html += `
    </tbody>
  `;

  table.innerHTML = html;
  return table;
}

/**
 * Refresh just the summary display (called when real-time values exceed stored values)
 */
function refreshSummaryDisplay() {
  const todayPanel = document.getElementById('summary-today');
  if (todayPanel && apiSummary) {
    todayPanel.innerHTML = '';
    todayPanel.appendChild(createSummaryTable(apiSummary, 'today'));
  }
}

/**
 * Create combined tabbed interface (Today | This Month | This Year | All Time)
 */
function createCombinedTabs(records, summary) {
  const container = document.createElement('div');
  container.className = 'records-main-container';

  // Tab navigation - all four periods in one row
  const tabNav = document.createElement('ul');
  tabNav.className = 'records-subtabs';
  tabNav.innerHTML = `
    <li class="records-subtab active" data-tab="today">Today</li>
    <li class="records-subtab" data-tab="month">This Month</li>
    <li class="records-subtab" data-tab="yearly">This Year</li>
    <li class="records-subtab" data-tab="alltime">All Time</li>
  `;

  // Tab content
  const tabContent = document.createElement('div');
  tabContent.className = 'records-subtab-content';

  // Today panel (summary)
  const todayPanel = document.createElement('div');
  todayPanel.className = 'records-subpanel active';
  todayPanel.id = 'summary-today';
  todayPanel.appendChild(createSummaryTable(summary, 'today'));

  // This Month panel (summary)
  const monthPanel = document.createElement('div');
  monthPanel.className = 'records-subpanel';
  monthPanel.id = 'summary-month';
  monthPanel.appendChild(createSummaryTable(summary, 'month'));

  // This Year panel (records)
  const yearlyPanel = document.createElement('div');
  yearlyPanel.className = 'records-subpanel';
  yearlyPanel.id = 'records-yearly';
  yearlyPanel.appendChild(createRecordsTable(records, 'yearly'));

  // All Time panel (records)
  const alltimePanel = document.createElement('div');
  alltimePanel.className = 'records-subpanel';
  alltimePanel.id = 'records-alltime';
  alltimePanel.appendChild(createRecordsTable(records, 'alltime'));

  tabContent.appendChild(todayPanel);
  tabContent.appendChild(monthPanel);
  tabContent.appendChild(yearlyPanel);
  tabContent.appendChild(alltimePanel);

  container.appendChild(tabNav);
  container.appendChild(tabContent);

  // Add tab click handlers
  tabNav.querySelectorAll('.records-subtab').forEach(tab => {
    tab.addEventListener('click', () => {
      tabNav.querySelectorAll('.records-subtab').forEach(t => t.classList.remove('active'));
      tabContent.querySelectorAll('.records-subpanel').forEach(p => p.classList.remove('active'));

      tab.classList.add('active');
      const tabId = tab.dataset.tab;
      // Map tab IDs to panel IDs
      const panelId = (tabId === 'today' || tabId === 'month')
        ? `summary-${tabId}`
        : `records-${tabId}`;
      const targetPanel = tabContent.querySelector(`#${panelId}`);
      if (targetPanel) {
        targetPanel.classList.add('active');
      }
    });
  });

  return container;
}

/**
 * Display records in the UI
 */
export function displayRecords(records, summary) {
  const container = document.getElementById('records-container');

  if (!records || !summary) {
    container.innerHTML = '<p class="text-center">No records available</p>';
    return;
  }

  // Store summary values for real-time comparison
  updateStoredSummary(summary);

  // Clear container and add combined tabbed interface
  container.innerHTML = '';
  container.className = 'records-container';

  const tabs = createCombinedTabs(records, summary);
  container.appendChild(tabs);
}

/**
 * Refresh records from API (called periodically and on init)
 */
async function refreshRecords() {
  const response = await fetchRecords();

  if (response && response.status === 'success' && response.records && response.summary) {
    displayRecords(response.records, response.summary);
    return true;
  } else {
    console.error('[Records] Failed to refresh records');
    return false;
  }
}

/**
 * Initialize records module
 */
export async function initializeRecords() {
  const success = await refreshRecords();

  if (!success) {
    const container = document.getElementById('records-container');
    container.innerHTML = '<div class="cell medium-12"><p class="text-center text-alert">Failed to load records</p></div>';
  }

  // Set up 30-minute refresh interval
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }
  refreshInterval = setInterval(refreshRecords, REFRESH_INTERVALS.RECORDS);
}

/**
 * Stop the refresh interval (for cleanup)
 */
export function stopRecordsRefresh() {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
}
